import { useState } from 'react';
import { makeStyles } from '@material-ui/core';

import { useDebounce } from '../../../shared/hooks/use-debounce.hook';
import { DEFAULT_ROWS_PER_PAGE } from '../../../shared/constants/table.constants';
import { MIN_SEARCH_LENGTH } from '../../../shared/constants/search.constants';
import { useAdminGetInvitations } from '../../hooks/admin-get-invitations.hook';
import { InvitationSortColumn, InvitationSortDirection } from '../../types/invitation.type';
import { InvitationsListFilters } from '../../components/Containers/InvitationsListFilters.component';
import { InvitationsTable } from '../../components/Tables/InvitationsTable.component';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '86svh',
    gap: '0.7rem',

    '& > h2': {
      margin: 0,
    },
  },
});

export default function AdminInvitationsView() {
  const classes = useStyles();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [deliveryDateFrom, setDeliveryDateFrom] = useState('');
  const [deliveryDateTo, setDeliveryDateTo] = useState('');
  const [sortBy, setSortBy] = useState<InvitationSortColumn>('deliveryDate');
  const [sortDirection, setSortDirection] = useState<InvitationSortDirection>('desc');

  const debouncedSearch = useDebounce(search, 400).trim();

  const { isLoading, isRefetching, totalCount, invitations } = useAdminGetInvitations({
    page: page + 1,
    limit: rowsPerPage,
    sortBy,
    sortDirection,
    filters: {
      ...(debouncedSearch.length >= MIN_SEARCH_LENGTH ? { search: debouncedSearch } : {}),
      ...(deliveryDateFrom ? { deliveryDateFrom } : {}),
      ...(deliveryDateTo ? { deliveryDateTo } : {}),
    },
  });

  const withPageReset =
    <Value,>(setValue: (value: Value) => void) =>
    (value: Value) => {
      setValue(value);
      setPage(0);
    };

  const handleSortChange = (column: InvitationSortColumn) => {
    setSortDirection(column === sortBy && sortDirection === 'desc' ? 'asc' : 'desc');
    setSortBy(column);
    setPage(0);
  };

  return (
    <section className={classes.root}>
      <h2>Invitations éco</h2>
      <InvitationsListFilters
        search={search}
        deliveryDateFrom={deliveryDateFrom}
        deliveryDateTo={deliveryDateTo}
        onSearchChange={withPageReset(setSearch)}
        onDeliveryDateFromChange={withPageReset(setDeliveryDateFrom)}
        onDeliveryDateToChange={withPageReset(setDeliveryDateTo)}
      />
      <InvitationsTable
        invitations={invitations}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        isLoading={isLoading || isRefetching}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onPageChange={setPage}
        onRowsPerPageChange={withPageReset(setRowsPerPage)}
        onSortChange={handleSortChange}
      />
    </section>
  );
}
